import { IChartData } from "@/components/charts/ChartTypes";
import { IStoresData } from "@/components/charts/country/stores-overview";
import { createServerEndpoint } from "@/utils/functions";
import axios from "axios";
import { IClosedCasesData } from "@/components/charts/country/closed-cases";
import ROUTE_URL from "@/router/router-constants";

const state = () => ({
  currentCasesReport: {} as IChartData,
  riskPatternReport: {} as IChartData,
  closedCasesReport: {} as IClosedCasesData,
  closedCasesBreakdownReport: {} as IClosedCasesData,
  storesOverviewReport: {} as IStoresData,
  currentCasesFetched: false,
  riskPatternFetched: false,
  closedCasesFetched: false,
  closedCasesBreakdownFetched: false,
  storeOverviewFetched: false,
  patternReportsFetched: false,
  embedParams: ""
});

const getters = {
  currentCases(state: any): IChartData {
    return state.currentCasesReport;
  },
  closedCases(state: any): IClosedCasesData {
    return state.closedCasesReport;
  },
  riskPattern(state: any): IChartData {
    return state.riskPatternReport;
  },
  storesOverview(state: any): IStoresData {
    return state.storesOverviewReport;
  }
};

const mutations = {
  UPDATE_CURRENT_CASES_REPORT(state: any, payload: IChartData) {
    state.currentCasesFetched = true;
    state.currentCasesReport = payload;
  },
  CURRENT_CASES_FETCH(state: any) {
    state.currentCasesFetched = false;
  },
  UPDATE_RISK_PATTERN(state: any, payload: IChartData) {
    state.riskPatternFetched = true;
    state.riskPatternReport = payload;
  },
  RISK_PATTERN_FETCH(state: any) {
    state.riskPatternFetched = false;
  },
  UPDATE_CLOSED_CASES_REPORT(state: any, payload: IClosedCasesData) {
    state.closedCasesFetched = true;
    state.closedCasesReport = payload;
  },
  CLOSED_CASES_FETCH(state: any) {
    state.closedCasesFetched = false;
  },
  UPDATE_CLOSED_CASES_BREAKDOWN_REPORT(state: any, payload: IClosedCasesData) {
    state.closedCasesBreakdownFetched = true;
    state.closedCasesBreakdownReport = payload;
  },
  CLOSED_CASES_BREAKDOWN_FETCH(state: any) {
    state.closedCasesBreakdownFetched = false;
  },
  UPDATE_STORES_OVERVIEW(state: any, payload: IStoresData) {
    state.storeOverviewFetched = true;
    state.storesOverviewReport = payload;
  },
  STORES_OVERVIEW_FETCH(state: any) {
    state.storeOverviewFetched = false;
  },
  PATTERN_REPORTS_FETCH(state: any) {
    state.patternReportsFetched = false;
  },
  STORES_OVERVIEW_FETCH_COMPLETE(state: any) {
    state.storeOverviewFetched = true;
  },
  PATTERN_REPORTS_FETCH_COMPLETE(state: any) {
    state.patternReportsFetched = true;
  },
  UPDATE_EMBED_PARAMS_PATTERNS(state: any, payload: any) {
    state.embedParams = payload;
  }
};

const actions = {
  async FETCH_CURRENT_CASES_REPORT({ commit }: any, req: any = null) {
    const endpoint = createServerEndpoint("/api/stats/cases");
    try {
      commit("CURRENT_CASES_FETCH");
      const { data } = await axios.get<IChartData>(endpoint, { params: req });
      commit("UPDATE_CURRENT_CASES_REPORT", data);
    } catch (error) {
      console.error(error);
      commit("UPDATE_CURRENT_CASES_REPORT", { labels: [], datasets: [] });
    }
  },

  async FETCH_RISK_PATTERN_BREAKDOWN_REPORT({ commit }: any, req: any = null) {
    const endpoint = createServerEndpoint("/api/stats/risk-pattern-breakdown");
    try {
      commit("RISK_PATTERN_FETCH");
      const { data } = await axios.get<IChartData>(endpoint, { params: req });
      commit("UPDATE_RISK_PATTERN", data);
    } catch (error) {
      console.error(error);
      commit("UPDATE_RISK_PATTERN", { labels: [], datasets: [] });
    }
  },

  async FETCH_CLOSED_CASES_REPORT({ commit }: any, req: any = null) {
    const endpoint = createServerEndpoint("/api/stats/closed-cases");
    try {
      commit("CLOSED_CASES_FETCH");
      const { data } = await axios.get<IClosedCasesData>(endpoint, {
        params: req
      });
      commit("UPDATE_CLOSED_CASES_REPORT", data);
    } catch (error) {
      console.error(error);
      commit("UPDATE_CLOSED_CASES_REPORT", {
        labels: [],
        datasets: [],
        avgDays: 0,
        numberSubstantiatedCategories: 0,
        labelsMeaning: []
      });
    }
  },

  async FETCH_CLOSED_CASES_BREAKDOWN_REPORT({ commit }: any, req: any = null) {
    const endpoint = createServerEndpoint("/api/stats/closed-cases-breakdown");
    try {
      commit("CLOSED_CASES_BREAKDOWN_FETCH");
      const { data } = await axios.get<IClosedCasesData>(endpoint, {
        params: req
      });
      commit("UPDATE_CLOSED_CASES_BREAKDOWN_REPORT", data);
    } catch (error) {
      console.error(error);
      commit("UPDATE_CLOSED_CASES_BREAKDOWN_REPORT", {
        labels: [],
        datasets: [],
        avgDays: 0,
        numberSubstantiatedCategories: 0,
        labelsMeaning: []
      });
    }
  },

  async FETCH_STORE_OVERVIEW_REPORT({ commit }: any, req: any = null) {
    const endpoint = createServerEndpoint("/api/stats/store-overview");
    try {
      commit("STORES_OVERVIEW_FETCH");
      const { data } = await axios.get<IStoresData>(endpoint, { params: req });
      commit("UPDATE_STORES_OVERVIEW", data);
    } catch (error) {
      console.error(error);
      commit("UPDATE_STORES_OVERVIEW", { datasets: [] });
    }
  },

  async FETCH_ALL_REPORTS({ dispatch }: any, params: any = null) {
    await dispatch("FETCH_CURRENT_CASES_REPORT", params);
    await dispatch("FETCH_RISK_PATTERN_BREAKDOWN_REPORT", params);
    await dispatch("FETCH_CLOSED_CASES_REPORT", params);
    await dispatch("FETCH_CLOSED_CASES_BREAKDOWN_REPORT", params);
    await dispatch("FETCH_STORE_OVERVIEW_REPORT", params);
  },

  async FETCH_EMBED_PARAMS({ commit }: any) {
    let result: any;
    try {
      const { data } = await axios.post<any>(
        createServerEndpoint(ROUTE_URL.ACTION_TOKEN_PATTERNS)
      );
      result = data;
      // const error = data
      //   ? null
      //   : {
      //       code: 500,
      //       summary: ERRORS.LOAD_ERROR
      //     };
      console.log("Embedded token response for Patterns reports");
      // this.context.commit(ControlsMutationEnum.REGISTER_PAGE_LOAD_ERROR, error);
      commit("PATTERN_REPORTS_FETCH_COMPLETE");
    } catch (error: any) {
      console.error("FETCH_EMBED_PARAMS", error);
      result = null;
      commit("PATTERN_REPORTS_FETCH_COMPLETE");
    }
    console.log("[UPDATE_EMBED_PARAMS_PATTERNS]");
    commit("UPDATE_EMBED_PARAMS_PATTERNS", result);
    // this.context.commit(ControlsMutationEnum.REGISTER_LOADING, false);
    return result;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
